// Head to Head Trends — aggregate H2H stat bars (Phase 3 → M2 wave 6).
//
// The stat-bar type (like Team Discipline / Referee Profile): six horizontal
// bars — Avg Goals, Over 1.5/2.5/3.5, BTTS, Draw % — over the last N seasons of
// meetings (seasons-toggle). The `.stat-bar-fill` carries a CSS `transition:
// width`, so setting the width inline in JSX animates it from its current value
// on a seasons change — no D3 needed (the legacy uses a d3 width transition; the
// settled DOM + pixels are identical, and Playwright fast-forwards the CSS
// transition for the VRT). Emits the SAME DOM + classes as the legacy
// renderH2HGoalsProfileModule: `.stat-row-label` + `.stat-row-val--wide`
// (verified by the baseline). Data now comes from app-api via `useWidgetData`
// (was the `getH2HData` getter — the same `h2h` slice Head to Head reads, under
// its own endpoint key); the shaper is unchanged. Seasons reshapes client-side.

import { shapeH2HTrends, statBarPct } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface HeadToHeadTrendsProps {
  fixture: string;
  /** Seasons toggle: "3" | "5" | "All"; defaults to "3". */
  seasons?: string | null;
}

export default function HeadToHeadTrends({
  fixture,
  seasons,
}: HeadToHeadTrendsProps) {
  const { data, isPending, isError, refetch } = useWidgetData(
    "head-to-head-trends",
    useFixtureKey(fixture)
  );

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const rows = shapeH2HTrends(
    data ?? {},
    fixture || "Arsenal v Everton",
    seasons || "3"
  );

  if (!rows) {
    return (
      <div className="data-wrapper top-align">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  return (
    <div className="data-wrapper top-align">
      {rows.map((row) => (
        <div className="stat-row" key={row.label}>
          <div className="stat-row-label">{row.label}</div>
          <div className="stat-bar-track">
            <div
              className="stat-bar-fill"
              style={{
                width: `${statBarPct(row.val, row.max)}%`,
                background: row.colour,
              }}
            />
          </div>
          <div className="stat-row-val stat-row-val--wide">{row.text}</div>
        </div>
      ))}
    </div>
  );
}
